//
// # Editor
//
// The "top level" thing driving the GUI, drawing logic, saving and loading logic.
//

// Local Imports
import { logger, msgs, Result, LoadKind } from "CoreUtils";
import { schema } from "GenAlphaSchema";
import { model, schdata, graphics } from "GenAlphaModel";
import { Platform } from "EditorCore/src/platform";
import { Keys, isTextInputFocused } from "EditorCore/src/keys";
import { UiState } from "./uistate";
import * as modes from "EditorCore/src/modes";
import { MousePos } from "DrawCore/src/mousepos";
import { Canvas } from "EditorCore/src/canvas";
import { Grid } from "EditorCore/src/grid";
import { Theme } from "DrawCore/src/style";
import { applyThemeToDom } from "./theme_dom";
import {
  ContentKind,
  type DrcErrorInfo,
  type LayoutMarker,
} from "DrawCore/src/content";
import { Content } from "./content/content";
import type { LvsErrorInfo } from "./content/layout/content";
import { Perf, PerfEntry } from "./perf";
import { SelfTest } from "./selftest";
import { Overlays } from "./overlays";
import { DefaultCanvas } from "./default_canvas";
import { DepotByMessage } from "./depot";
import { InstanceTooltip } from "./tooltip";
import { InstanceContextMenu, RouteContextMenu } from "./contextMenu";
import { SchematicInstanceContextMenu } from "./schematicContextMenu";
import { EmptySpaceContextMenu } from "./emptySpaceContextMenu";
import { SymbolPinContextMenu } from "./symbolPinContextMenu";
import { LayoutPinContextMenu } from "./layoutPinContextMenu";
import { SymbolExprLabelContextMenu } from "./symbolExprLabelContextMenu";
import { PinContextMenu } from "./pinContextMenu";
import { WireContextMenu } from "./wireContextMenu";
import { TextAnnotationContextMenu } from "./textAnnotationContextMenu";
import { LayerPickerMenu } from "./layerPickerMenu";
import * as changes from "DrawCore/src/changes";
import { LayoutContent } from "EditorCore/src/content/layout/content";
import { Schematic } from "EditorCore/src/content/schematic/schematic";
import { type SrvOverlay } from "DrawCore/src/content/schematic/srv_arrows";
import { EditorViewport } from "./editor_viewport";
import { SymbolPin, ExprLabel } from "DrawCore/src/content/shared";
import { pinLabelFmt } from "EditorCore/src/content/symbol/symbol";
import {
  AddSymbolPin,
  AddExprLabel,
  AddCatalogueShape,
} from "EditorCore/src/content/symbol/modes";
import { DrawRoute } from "EditorCore/src/content/layout/draw_route";
import type { InteractionHost } from "EditorCore/src/interaction_host";

export interface EditorOptions {
  events?: boolean; // Whether to bind events. Generally `true`, but `false` for testing.
}

// # Redraw Request
// The marker type now lives in DrawCore (`redraw.ts`); re-exported here so the
// browser modules that still import it from `editor` keep working.
import type { RedrawRequest } from "DrawCore/src/redraw";
export type { RedrawRequest };

//
// # Editor
//
// The "top-level" for the editor UI, including all UI state and the content under edit.
//
// The Platform is responsible for tasks such as file I/O and launching the editor.
// Editor communicates with Platform via the Platform interface:
// - Data access: `platform.getPdk()`, `platform.getStack()`, `platform.getContent()`
// - Notifications: `platform.notifyUIChange()`, `platform.log()`, etc.
//
// The Platform calls methods on Editor (via `platform.setEditor(this)`):
// - `loadMainContent()`, `createNew()`, `updateSettings()`, etc.
//
// For message-based environments (e.g. Web), MessagePlatform implements
// Platform on top of a Transport, handling all message serialization.
//
export class Editor implements InteractionHost {
  content: Content = Content.default(this); // The content under design
  modeHandler: modes.ModeHandler = this.content.idleMode(); // Mode-specific UI handler
  uiState: UiState = new UiState(this); // Non-schematic UI state
  perf: Perf = new Perf();
  selfTester: SelfTest | null = null; // Self-test instance
  readonly overlays: Overlays = new Overlays(this);
  readonly depot: DepotByMessage = new DepotByMessage(this);
  readonly instanceTooltip: InstanceTooltip = new InstanceTooltip(this);
  readonly instanceContextMenu: InstanceContextMenu = new InstanceContextMenu(
    this
  );
  readonly schematicInstanceContextMenu: SchematicInstanceContextMenu =
    new SchematicInstanceContextMenu(this);
  readonly emptySpaceContextMenu: EmptySpaceContextMenu =
    new EmptySpaceContextMenu(this);
  readonly symbolPinContextMenu: SymbolPinContextMenu =
    new SymbolPinContextMenu(this);
  readonly layoutPinContextMenu: LayoutPinContextMenu =
    new LayoutPinContextMenu(this);
  readonly symbolExprLabelContextMenu: SymbolExprLabelContextMenu =
    new SymbolExprLabelContextMenu(this);
  readonly pinContextMenu: PinContextMenu = new PinContextMenu(this);
  readonly wireContextMenu: WireContextMenu = new WireContextMenu(this);
  readonly textAnnotationContextMenu: TextAnnotationContextMenu =
    new TextAnnotationContextMenu(this);
  readonly routeContextMenu: RouteContextMenu = new RouteContextMenu(this);
  readonly layerPickerMenu: LayerPickerMenu = new LayerPickerMenu(this);

  // Hide all context menus. Called before showing a new one so that only
  // one context menu is ever visible at a time.
  hideAllContextMenus = (): void => {
    this.instanceContextMenu.hide();
    this.schematicInstanceContextMenu.hide();
    this.emptySpaceContextMenu.hide();
    this.symbolPinContextMenu.hide();
    this.layoutPinContextMenu.hide();
    this.symbolExprLabelContextMenu.hide();
    this.pinContextMenu.hide();
    this.wireContextMenu.hide();
    this.textAnnotationContextMenu.hide();
    this.routeContextMenu.hide();
    this.layerPickerMenu.hide();
  };

  // Whether any context menu is currently visible.
  // Used to suppress canvas interactions (e.g. box-select) when the user
  // clicks on the canvas to dismiss an open context menu.
  isAnyContextMenuVisible = (): boolean => {
    return (
      this.instanceContextMenu.isVisible() ||
      this.schematicInstanceContextMenu.isVisible() ||
      this.emptySpaceContextMenu.isVisible() ||
      this.symbolPinContextMenu.isVisible() ||
      this.layoutPinContextMenu.isVisible() ||
      this.symbolExprLabelContextMenu.isVisible() ||
      this.pinContextMenu.isVisible() ||
      this.wireContextMenu.isVisible() ||
      this.textAnnotationContextMenu.isVisible() ||
      this.layerPickerMenu.isVisible()
    );
  };

  // Whether the editor is currently in route-drawing mode.
  // Used by the IDE's KeyboardHelpPanel to filter shortcuts by mode.
  get isRouteMode(): boolean {
    return this.modeHandler instanceof DrawRoute;
  }

  trackpadMode: boolean = false; // Toggle with Shift+T; changes scroll/zoom input interpretation
  showOutlines: boolean = false; // Toggle with Shift+O; shows layout cell outlines
  showAbstractView: boolean = false; // Toggle with 8 key; shows simplified abstract floorplan (colored bboxes + centered names)

  // When true, the editor is locked for editing (e.g., LLM query in flight).
  // Navigation (pan, zoom, fit) still works; design-modifying interactions are blocked.
  isLocked = false;

  // Loading and rendering timing flags:
  fullyRendered: boolean = false; // Set false on new content, set to true after redraw. Test waits for this to start.
  private loadPending: boolean = false; // Set to true during load, false after content is loaded.
  private timeStartUp: number = 0; // Time that we started loading at.
  private elapsedStartupLoad: number = 0; // Time that we took to load.
  private elapsedStartUpToRender: number = 0; // How long we took to load and render.
  private pacingSmoothed: number = 0; // Smoothed FPS time.

  // Reset the frame stats
  private resetFrameStats = () => {
    this.perf.clear();
    this.pacingSmoothed = 0;
  };

  private beginLoadTiming() {
    // Startup timer:
    this.fullyRendered = false;
    this.loadPending = true;
    this.timeStartUp = performance.now();
  }

  // Redraw Request Handling
  // Each call to `requestRedraw` will schedule a new frame, if one is not already scheduled.
  readonly onFrame = () => {
    if (this.redrawRequested) {
      this.redraw();
      this.redrawRequested = false;
    }
    this.frameId = null;
  };
  private frameId: number | null = requestAnimationFrame(this.onFrame);
  private redrawRequested = true; // Whether a redraw has been requested. Start with it true to generate a first-time draw.
  private feedbackTimer: ReturnType<typeof setTimeout> | null = null; // Timer for auto-clearing feedback messages
  readonly requestRedraw = (): RedrawRequest => {
    this.redrawRequested = true;
    if (this.frameId === null) {
      this.frameId = requestAnimationFrame(this.onFrame);
    }
    return {} as RedrawRequest;
  };

  canvas: Canvas; // The drawing canvas
  viewport: EditorViewport; // Viewport reveal/centering control
  grid: Grid; // The background `Grid`

  constructor(
    readonly platform: Platform, // Platform interface
    readonly parentDomElement: HTMLDivElement, // Parent HTML DOM element
    readonly options: EditorOptions,
    // Spec for the canvas. Either accepts the object itself, meeting the `Canvas` interface,
    // or a set of options passed to the default version.
    canvas?:
      | { kind: "object"; object: Canvas }
      | { kind: "options"; options: unknown }
  ) {
    logger.log(`Starting Editor with platform '${this.platform.name}'`);

    // Set up the canvas
    if (canvas && canvas.kind === "object") {
      this.canvas = canvas.object;
    } else {
      this.canvas = DefaultCanvas.fromOptions(this, canvas?.options);
    }
    this.viewport = new EditorViewport(this.canvas);
    this.grid = Grid.empty(this);
    if (options.events) this.setupEvents();
  }

  // Set up all of our UI events.
  // "Events" is underselling this a bit; it's more like "set up everything with `window` and `document`".
  // Which in some testing cases we don't wanna do.
  setupEvents = () => {
    // Set some global "styling", to make the canvas full-screen, and disable HTML-based selection
    const style: CSSStyleDeclaration = document.body.style;

    // Theme initialization is handled by the IDE's UserPreferences store,
    // which calls editor.setTheme() with the resolved light/dark value.

    // Disable HTML-based selection.
    style.userSelect = "none";
    style.cursor = "default";

    // Keep the ugly border that HTML puts by default from screwing up our nice full screen canvas
    style.margin = "0";
    style.padding = "0";

    // Fonts: eh.
    // style.fontFamily = '-apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, Helvetica, Arial, sans-serif';

    // FIXME: these have never really looked right.
    // // Register the VSCode design system, and the components we use.
    // import {
    //   provideVSCodeDesignSystem,
    //   vsCodeButton,
    //   vsCodeTextArea,
    // } from "@vscode/webview-ui-toolkit";

    // provideVSCodeDesignSystem().register(
    //   vsCodeButton(), //
    //   vsCodeTextArea() //
    // );

    // Perform all of our one-time startup activity, binding the canvas to the DOM, binding events, etc.
    // FIXME: probably should just be in dev mode, which this package doesn't (yet) know about.
    window.genAlphaEditor = this;

    // Set up all of our event listeners
    const e = this.parentDomElement;
    e.addEventListener("mousedown", this.handleMouseDown, false);
    e.addEventListener("mousemove", this.handleMouseMove, false);
    e.addEventListener("dblclick", this.handleDoubleClick, false);

    // These events, in contrast, get attached to the `window` or `document`.
    // The key event listener seems to need to be on `window`, while mouse events are on the canvas's parent div.
    window.addEventListener("keydown", this.handleKey);
    window.addEventListener("resize", this.handleResize);
    // Setting mouse-up on `document` ensures we get mouse-release events even outside our app.
    document.addEventListener("mouseup", this.handleMouseUp, true);
    // Disable the default browser context menu - we use our own custom context menu
    document.oncontextmenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Wheel / Scroll / Zoom Events
    // Note there are several pieces of care required to keep the browser and/or SVG from
    // doing its own pinch-handling, and zooming independent of our own zooming.
    // This bit about "passive events" (whatever they are) seems to be the key:
    // https://github.com/WICG/interventions/issues/64
    this.parentDomElement.addEventListener("wheel", this.handleWheel, {
      passive: false,
    });

    // Initialize the color theme to the default (dark).
    // OS/browser preferences are intentionally ignored.
    this.setTheme(Theme.get());

    // Add our overlays to the parent DOM element.
    // This is not exactly an "event", but is never visible
    // if events are not enabled.
    e.appendChild(this.overlays.div);

    // Register this Editor with the Platform.
    // This allows the Platform to call methods on us (loadMainContent, etc.)
    this.platform.setEditor(this);

    // Notify the platform that we're ready.
    this.platform.notifyEditorUp();
  };

  log = (body: unknown) => {
    this.platform.log(body);
  };
  warn = (body: unknown) => {
    this.platform.warn(body);
  };
  error = (body: unknown) => {
    this.platform.error(body);
  };

  handleResize = (_: UIEvent) =>
    this.canvas.sizeToElement(this.parentDomElement);

  // Handle a color-scheme change, generally via a `MediaQueryListEvent`.
  // The only field we care about is whether it `matches` the "prefers dark" query we set up.
  handleTheme = (darkMatcher: { matches: boolean }) => {
    const theme = darkMatcher.matches ? Theme.Dark : Theme.Light;
    this.setTheme(theme);
  };
  setTheme = (theme: Theme): RedrawRequest => {
    Theme.set(theme);
    applyThemeToDom(theme);
    // Update UI overlay styles for the new theme
    this.instanceTooltip.updateTheme();
    this.instanceContextMenu.updateTheme();
    this.schematicInstanceContextMenu.updateTheme();
    this.emptySpaceContextMenu.updateTheme();
    this.symbolPinContextMenu.updateTheme();
    this.symbolExprLabelContextMenu.updateTheme();
    this.pinContextMenu.updateTheme();
    this.wireContextMenu.updateTheme();
    this.textAnnotationContextMenu.updateTheme();
    this.routeContextMenu.updateTheme();
    this.layerPickerMenu.updateTheme();
    // Invalidate any cached stuff in both the canvas and the content.
    this.canvas.invalidate?.();
    return this.content.invalidate();
  };
  toggleTheme = (): RedrawRequest => {
    // Toggle between light and dark.  The IDE uses its own Light/Dark/Auto
    // cycle via UserPreferences and calls setTheme() directly; this method
    // is kept for the VSCode extension which still calls it.
    Theme.toggle();
    return this.setTheme(Theme.get());
  };
  // Pin label, instance label, and route label toggling is now handled via
  // LayoutContent's display option flags and the IDE's LayerPanel checkboxes /
  // CommandCenter keyboard shortcuts.

  // Toggle the abstract floorplan view (colored bboxes + centered names).
  // This lives on Editor rather than LayoutContent because it changes the
  // rendering mode of the entire editor, not just a metadata overlay.
  toggleAbstractView = (): RedrawRequest => {
    this.showAbstractView = !this.showAbstractView;
    this.canvas.invalidate?.();
    return this.requestRedraw();
  };

  // Return the content as a LayoutContent if the editor is in layout mode, or null otherwise.
  getLayoutContent = (): LayoutContent | null => {
    if (this.content.contentKind === "layout") {
      return this.content as LayoutContent;
    }
    return null;
  };

  // Return the content as a Schematic if the editor is in schematic mode, or null otherwise.
  getSchematicContent = (): Schematic | null => {
    if (this.content.contentKind === "schematic") {
      return this.content as Schematic;
    }
    return null;
  };

  // Get the main content as schema data
  getMainContent = (): schema.Content => {
    return model.Content.toSchema(this.content.toData());
  };

  // Add a symbol pin with the given configuration from the Add Pin dialog.
  // Called by Workspace after the user confirms the dialog.
  addSymbolPinWithConfig = (
    position: { x: number; y: number },
    config: {
      portName: string;
      width: number;
      dir: schdata.PortDir;
      shape: string;
      labelPosition?: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Symbol) return;
    const symbol = this.content;

    // Build the pin shape based on the selected kind
    let pinShape: schdata.PinShape;
    if (config.shape === "ellipse") {
      pinShape = model.graphics.Ellipse.new({
        bbox: {
          min: { x: position.x - 5, y: position.y - 5 },
          max: { x: position.x + 5, y: position.y + 5 },
        },
        filled: true,
      });
    } else {
      pinShape = schdata.PinShape.fromCenter(position);
    }

    const pinData: schdata.SymbolPin = {
      portName: config.portName,
      width: config.width,
      direction: config.dir,
      shape: pinShape,
      labelFmt: pinLabelFmt(position, config.labelPosition),
    };

    const pin = new SymbolPin({ data: pinData, parent: symbol.render });
    AddSymbolPin.enterWith(symbol, pin);
  };

  // Edit an existing symbol pin's properties.
  // Called by Workspace after the user confirms the Edit Pin dialog.
  editSymbolPinWithConfig = (
    pinId: number,
    config: {
      portName: string;
      width: number;
      dir: schdata.PortDir;
      labelPosition?: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Symbol) return;
    const symbol = this.content;

    // Find the pin by ID
    const pin = symbol.render.pins.get(pinId);
    if (!pin) return;

    // Snapshot old data for undo
    const oldData = schdata.SymbolPin.clone(pin.data);

    // Recompute label format relative to the current pin shape center
    const center = schdata.PinShape.bboxCenter(pin.data.shape);
    const newData: schdata.SymbolPin = {
      ...pin.data,
      portName: config.portName,
      width: config.width,
      direction: config.dir,
      labelFmt: pinLabelFmt(center, config.labelPosition),
    };

    // Apply the update and log for undo/redo
    pin.update(newData);
    symbol.logChange(
      changes.update({
        target: pin,
        from: oldData,
        to: schdata.SymbolPin.clone(newData),
      })
    );
  };

  // Add a layout pin with the given configuration from the Add Layout Pin dialog.
  // Called by Workspace after the user confirms the dialog.
  addLayoutPinWithConfig = (
    position: { x: number; y: number },
    config: {
      name: string;
      layer: number;
      pinType: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Layout) return;
    const layoutContent = this.content;

    // Resolve the layer number to a MetalLayer via the stack.
    // The content's `data` is a layout.Layout which holds the stack reference.
    // Safety: we access .data and .stack which exist on LayoutContent.
    const layoutData = (
      layoutContent as unknown as { data: model.layout.Layout }
    ).data;
    const { stack } = layoutData;
    if (!stack) {
      logger.warn("[Editor] Cannot add layout pin: no stack available");
      return;
    }
    const metalLayerResult = model.layout.Stack.findMetalLayer(
      stack,
      config.layer
    );
    if (!metalLayerResult.ok) {
      logger.warn(
        `[Editor] Cannot add layout pin: layer ${config.layer} not found in stack`
      );
      return;
    }

    // The dialog gives a point and a layer, and no metal: the pin is a point
    // shape there.
    const metal = metalLayerResult.val;
    const pinData: model.layout.LayoutPin = {
      ...schema.layoutPin(
        config.name,
        schema.pinPoint(metal.layer, metal.purpose, {
          x: position.x,
          y: position.y,
        })
      ),
      pinType: config.pinType as schema.core.PinType,
    };

    const pin = layoutContent.layoutEntity.createPin(pinData);
    layoutContent.addEntity(pin, true);
    layoutContent.logChange(changes.Add.new(pin));
    layoutContent.select(pin);
    // Cursor-attached placement (see enterPlacementMode), like the add-pin flow.
    this.enterPlacementMode();
  };

  // Edit an existing layout pin's properties.
  // Called by Workspace after the user confirms the Edit Layout Pin dialog.
  editLayoutPinWithConfig = (
    pinId: number,
    config: {
      name: string;
      layer: number;
      pinType: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Layout) return;
    const layoutContent = this.content;

    // Find the pin by ID
    const pin = layoutContent.layoutEntity.findPin(pinId);
    if (!pin) return;

    // Resolve the layer number to a MetalLayer
    const layoutData = (
      layoutContent as unknown as { data: model.layout.Layout }
    ).data;
    const { stack } = layoutData;
    if (!stack) return;
    const metalLayerResult = model.layout.Stack.findMetalLayer(
      stack,
      config.layer
    );
    if (!metalLayerResult.ok) return;
    // The layer is a one-shape pin's; one with none or several has no one
    // layer to set.
    const sole = schema.pinSoleShape(pin.data);
    if (sole.err) {
      logger.warn(`[Editor] Cannot set the layer: ${sole.val.message}`);
      return;
    }

    // Snapshot old data for undo
    const oldData = { ...pin.data };
    const newData: model.layout.LayoutPin = {
      ...pin.data,
      name: config.name,
      shapes: [{ ...sole.val, layer: metalLayerResult.val.layer }],
      pinType: config.pinType as schema.core.PinType,
    };

    // Apply the update and log for undo/redo
    pin.update(newData);
    layoutContent.logChange(
      changes.update({
        target: pin,
        from: oldData,
        to: { ...newData },
      })
    );
  };

  // Add an expression label with the given configuration from the Add Expr Label dialog.
  // Called by Workspace after the user confirms the dialog.
  addExprLabelWithConfig = (
    position: { x: number; y: number },
    config: {
      kind: string;
      name?: string;
      paramName?: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Symbol) return;
    const symbol = this.content;

    // Build the expression data based on the selected kind
    const baseFmt = {
      loc: position,
      height: 16,
      hidden: false,
      alignment: "left" as const,
      baseline: "middle" as const,
      rotation: 0 as const,
    };

    let exprData: schdata.Expr;
    switch (config.kind) {
      case "cell-name":
        exprData = { ...baseFmt, kind: "cell-name" as const };
        break;
      case "inst-name":
        exprData = { ...baseFmt, kind: "inst-name" as const };
        break;
      case "port-name":
        if (config.name === undefined) {
          logger.warn("port-name expression label requires a name");
          return;
        }
        exprData = {
          ...baseFmt,
          kind: "port-name" as const,
          name: config.name,
        };
        break;
      case "inst-param":
        if (config.paramName === undefined) {
          logger.warn("inst-param expression label requires a paramName");
          return;
        }
        exprData = {
          ...baseFmt,
          kind: "inst-param" as const,
          spec: { kind: "name" as const, name: config.paramName },
        };
        break;
      default:
        logger.warn("Unknown expression label kind:", config.kind);
        return;
    }

    const exprLabel = new ExprLabel({ data: exprData, parent: symbol.render });
    AddExprLabel.enterWith(symbol, exprLabel);
  };

  // Edit an existing expression label's properties.
  // Called by Workspace after the user confirms the Edit Expr Label dialog.
  editExprLabelWithConfig = (
    labelId: number,
    config: {
      kind: string;
      name?: string;
      paramName?: string;
    }
  ): void => {
    if (this.content.contentKind !== ContentKind.Symbol) return;
    const symbol = this.content;

    const label = symbol.render.exprLabels.get(labelId);
    if (!label) return;

    // Preserve the original text-format fields (loc, height, alignment, etc.)
    // and id; only swap the kind-discriminated fields.
    const oldData = label.data;
    const baseFmt = {
      loc: oldData.loc,
      height: oldData.height,
      hidden: oldData.hidden,
      alignment: oldData.alignment,
      baseline: oldData.baseline,
      rotation: oldData.rotation,
      id: oldData.id,
    };

    let newData: schdata.Expr;
    switch (config.kind) {
      case "cell-name":
        newData = { ...baseFmt, kind: "cell-name" as const };
        break;
      case "inst-name":
        newData = { ...baseFmt, kind: "inst-name" as const };
        break;
      case "port-name":
        if (config.name === undefined) {
          logger.warn("port-name expression label requires a name");
          return;
        }
        newData = {
          ...baseFmt,
          kind: "port-name" as const,
          name: config.name,
        };
        break;
      case "inst-param":
        if (config.paramName === undefined) {
          logger.warn("inst-param expression label requires a paramName");
          return;
        }
        newData = {
          ...baseFmt,
          kind: "inst-param" as const,
          spec: { kind: "name" as const, name: config.paramName },
        };
        break;
      default:
        logger.warn("Unknown expression label kind:", config.kind);
        return;
    }

    // Same-kind edit: update in place. The kind-dependent cached state on the
    // entity (`LabelBase.table`/`cache`, which drives text colour) is still
    // valid, and entity identity is preserved for anything holding a reference.
    if (oldData.kind === newData.kind) {
      label.update(newData);
      symbol.requestRedraw();
      symbol.logChange(
        changes.update({ target: label, from: oldData, to: newData })
      );
      return;
    }

    // Kind changed: rebuild the entity. `update()` spread-merges, which would
    // leave stale variant-specific fields (e.g. an old `name` from port-name
    // when switching to cell-name). Direct assignment fixes that but doesn't
    // refresh the kind-dependent style cache, so the text would render in the
    // old kind's colour until the entity is rebuilt.
    const newLabel = new ExprLabel({ data: newData, parent: symbol.render });
    const wasSelected = symbol.selection.has(label);
    if (wasSelected) symbol.deselect();
    symbol.removeEntity(label, false);
    symbol.addEntity(newLabel, false);
    if (wasSelected) symbol.select(newLabel);
    symbol.requestRedraw();
    symbol.logChange(
      changes.Combo.new(changes.Remove.new(label), changes.Add.new(newLabel))
    );
  };

  // Add graphics elements from the catalogue in provisional mode.
  // Elements follow the mouse until the user clicks to commit.
  addCatalogueShape = (elements: Array<graphics.Element>): void => {
    if (this.content.contentKind !== ContentKind.Symbol) return;
    const symbol = this.content;
    AddCatalogueShape.enter(symbol, elements);
  };

  // Send the main content response to the platform (for GetMainContentRequest)
  sendMainContentResponse = (msg: msgs.GetMainContentRequest) => {
    const content = this.getMainContent();
    this.platform.sendMainContentResponse(msg.uuid, content);
  };

  // Send the main content to the platform (without a request, e.g., for save)
  sendMainContent = () => {
    const content = this.getMainContent();
    this.platform.sendMainContent(content);
  };

  // Send a change-message back to the platform (VSCode AlphaServer).
  sendChangeMessage = <T extends changes.ChangeEntity>(
    change: changes.Change<T>,
    contentKind: string
  ) => {
    // Convert Editor's class-based change to GenAlphaModel change format
    const modelChanges = this.convertToModelChanges(change, contentKind);
    if (modelChanges.length === 0) {
      return this.warn("Failed to convert change to model format");
    }

    // Notify the platform of each change
    for (const modelChange of modelChanges) {
      this.platform.notifyUIChange(modelChange);
    }
  };

  // Convert Editor's class-based change to GenAlphaModel change format.
  // Returns an array because Combo changes expand into multiple model changes.
  private convertToModelChanges<T extends changes.ChangeEntity>(
    change: changes.Change<T>,
    contentKind: string
  ): Array<model.Change> {
    try {
      switch (change.kind) {
        case changes.Kind.Add: {
          const addChange = change;
          const entity = addChange.entity;

          // Delegate to entity to provide its model data.
          // ID is not required for Add changes - the server assigns one if missing.
          const entityData = this.entityToModelData(entity, contentKind, false);
          if (!entityData) return [];

          return [this.makeAddChange(contentKind, entityData)];
        }

        case changes.Kind.Remove: {
          const removeChange = change;
          const entity = removeChange.entity;

          // Get ID and target kind from entity
          const entityId = this.getEntityId(entity);
          const targetKind = this.getTargetKind(entity, contentKind);
          if (entityId === undefined || !targetKind) {
            logger.warn("Cannot get ID or target kind from entity for removal");
            return [];
          }

          return [this.makeRemoveChange(contentKind, targetKind, entityId)];
        }

        case changes.Kind.Update: {
          const updateChange = change;

          if (!updateChange.target || !updateChange.to) {
            logger.warn("Update change missing target or to data");
            return [];
          }

          const entity = updateChange.target;
          const toData = updateChange.to;
          const entityId = this.getEntityId(entity);
          const targetKind = this.getTargetKind(entity, contentKind);

          if (entityId === undefined || !targetKind) {
            logger.warn("Cannot get ID or target kind from entity for update");
            return [];
          }

          // Serialize route path: MetalLayer objects → layerId references (same as Add path).
          let serializedToData =
            targetKind === "route" &&
            toData &&
            Array.isArray((toData as any).path)
              ? {
                  ...toData,
                  path: (toData as any).path.map(
                    (rp: {
                      point: unknown;
                      layer: { layer: number };
                      width?: number | null;
                    }) => ({
                      point: rp.point,
                      layerId: rp.layer.layer,
                      // Carry per-segment width to the wire/schema (else save drops it).
                      width: rp.width,
                    })
                  ),
                }
              : toData;

          // For layout instances, convert runtime BoundTarget/UnboundTarget back
          // to a schema Ident (same as the Add path in entityToModelData).
          if (
            contentKind === "layout" &&
            targetKind === "instance" &&
            (serializedToData as any).target
          ) {
            const runtimeTarget = (serializedToData as any).target;
            const schemaTarget =
              runtimeTarget.kind === "bound" && runtimeTarget.layout
                ? runtimeTarget.layout.ident
                : runtimeTarget.ident;
            const { target: _rt, ...rest } = serializedToData as any;
            serializedToData = { ...rest, target: schemaTarget };
          }

          // Selection boxes store a graphics.Rectangle (`bbox`) internally, but
          // the schema SelectionBbox is flat { min, max }. Convert on update to
          // match the Add path (entityToModelData) so move/resize edits
          // round-trip instead of merging a stray `bbox` field server-side.
          if (
            targetKind === "selectionbox" &&
            typeof serializedToData === "object" &&
            "bbox" in serializedToData
          ) {
            const { bbox, ...rest } = serializedToData;
            if (
              bbox &&
              typeof bbox === "object" &&
              "min" in bbox &&
              "max" in bbox
            ) {
              serializedToData = { ...rest, min: bbox.min, max: bbox.max };
            }
          }

          // Layout pins store `layer` as a full MetalLayer object internally,
          // but the schema expects a numeric layer ID.
          if (
            contentKind === "layout" &&
            targetKind === "pin" &&
            typeof serializedToData === "object" &&
            "layer" in serializedToData
          ) {
            const { layer, ...rest } = serializedToData;
            if (
              layer &&
              typeof layer === "object" &&
              "layer" in layer &&
              typeof layer.layer === "number"
            ) {
              serializedToData = { ...rest, layer: layer.layer };
            }
          }

          const updateData = { targetKind, ...serializedToData };

          return [this.makeUpdateChange(contentKind, entityId, updateData)];
        }

        case changes.Kind.Combo: {
          const comboChange = change;
          // Flatten all sub-changes so that every Add/Remove/Update in the
          // combo reaches the server. Wire split/merge operations produce
          // combos with multiple changes that must all be applied.
          return comboChange.changes.flatMap((c) =>
            this.convertToModelChanges(c, contentKind)
          );
        }

        default: {
          const _exhaustiveCheck: never = change;
          logger.warn("Unknown change kind:", _exhaustiveCheck);
          return [];
        }
      }
    } catch (error) {
      logger.error("Error converting change to model format:", error);
      return [];
    }
  }

  // Construct the correct model change based on content kind
  // Safety: uses `any` because entity data types vary per content kind
  private makeAddChange(
    contentKind: string,
    entityData: unknown
  ): model.Change {
    if (contentKind === "layout" || contentKind === "Layout") {
      return model.LayoutChange.add(entityData as any);
    } else if (contentKind === "symbol") {
      return model.SymbolChange.add(entityData as any);
    } else {
      return model.SchematicChange.add(entityData as any);
    }
  }

  private makeRemoveChange(
    contentKind: string,
    targetKind: string,
    entityId: number
  ): model.Change {
    if (contentKind === "layout" || contentKind === "Layout") {
      return model.LayoutChange.remove(targetKind as any, entityId);
    } else if (contentKind === "symbol") {
      return model.SymbolChange.remove(targetKind as any, entityId);
    } else {
      return model.SchematicChange.remove(targetKind as any, entityId);
    }
  }

  private makeUpdateChange(
    contentKind: string,
    entityId: number,
    updateData: unknown
  ): model.Change {
    if (contentKind === "layout" || contentKind === "Layout") {
      return model.LayoutChange.update(entityId, updateData as any);
    } else if (contentKind === "symbol") {
      return model.SymbolChange.update(entityId, updateData as any);
    } else {
      return model.SchematicChange.update(entityId, updateData as any);
    }
  }

  // Get entity data for model format - delegate to entity's data structure.
  // Converts runtime data (with resolved references) back to schema-compatible format.
  // Bridges untyped class-based Entity<T> objects (whose T varies per entity kind) to
  // the schema's JSON change format.
  //
  // Safety: uses `any` because this function bridges untyped class-based Entity<T>
  // objects (whose T varies per entity kind) to the schema's JSON change format.
  // The `any` lifetime ends at the return — callers pass the result to model.LayoutChange.add()
  // or model.SchematicChange.add() which are typed.
  // Convert an Entity to model data for a change message.
  // `idRequired` is true for Remove/Update changes (which reference existing entities)
  // and false for Add changes (where the server assigns an ID if missing).
  private entityToModelData(
    entity: any,
    contentKind: string,
    idRequired: boolean = true
  ): unknown | null {
    if (!entity?.data) {
      logger.error("Entity missing required data field for model conversion");
      return null;
    }

    const actualEntity = entity.entityKind ? entity : entity.parent;
    if (!actualEntity?.entityKind) {
      logger.error("Entity missing required entityKind for model conversion");
      return null;
    }

    const targetKind = this.getTargetKind(actualEntity, contentKind);
    if (!targetKind) {
      logger.error(
        "Unknown entity kind for model conversion:",
        actualEntity.entityKind
      );
      return null;
    }

    // Entity must have ID for Remove/Update changes.
    // For Add changes, the server will assign a new ID if undefined.
    const entityId = actualEntity.id ?? actualEntity.data.id;
    if (entityId === undefined && idRequired) {
      logger.error("Entity missing required ID for model conversion");
      return null;
    }

    // For layout instances, convert the runtime BoundTarget/UnboundTarget back
    // to a schema Ident. The runtime data has target: { kind: "bound", layout: Layout }
    // but the schema expects target: Ident (e.g. { kind: "libcellview", ... }).
    const isLayout = contentKind === "layout";
    if (isLayout && targetKind === "instance" && actualEntity.data.target) {
      const runtimeTarget = actualEntity.data.target;
      const { target: _runtimeTarget, ...rest } = actualEntity.data;
      const schemaTarget =
        runtimeTarget.kind === "bound" && runtimeTarget.layout
          ? runtimeTarget.layout.ident
          : runtimeTarget.ident;
      return {
        targetKind,
        id: entityId,
        ...rest,
        target: schemaTarget,
      };
    }

    // Selection boxes: the entity wraps a graphics.Rectangle internally,
    // but the schema expects SelectionBbox (min, max, id). Extract just the bbox.
    if (targetKind === "selectionbox") {
      const { bbox } = actualEntity.data;
      return {
        targetKind,
        id: entityId,
        min: bbox.min,
        max: bbox.max,
      };
    }

    // Serialize entity data. Routes store path points with MetalLayer objects internally
    // but the schema (and AlphaServer) expect path points as { point, layerId: number }.
    // Blockages and pins store layer as a MetalLayer object internally
    // but the schema expects a numeric layer ID.
    const data = actualEntity.data;
    let serializedData: object = data;
    if (targetKind === "route" && Array.isArray(data.path)) {
      serializedData = {
        ...data,
        path: data.path.map(
          (rp: {
            point: unknown;
            layer: { layer: number };
            width?: number | null;
          }) => ({
            point: rp.point,
            layerId: rp.layer.layer,
            // Carry per-segment width to the wire/schema (else save drops it).
            width: rp.width,
          })
        ),
      };
    } else if (targetKind === "pin" && isLayout && data.layer) {
      // Layout pins store layer as a full MetalLayer object internally
      // but the schema expects a numeric layer ID.
      const layerObj = data.layer;
      const layerNum =
        typeof layerObj === "object" &&
        "layer" in layerObj &&
        typeof layerObj.layer === "number"
          ? layerObj.layer
          : undefined;
      if (layerNum === undefined) {
        logger.error("Pin entity missing numeric layer ID");
        return null;
      }
      serializedData = { ...data, layer: layerNum };
    } else if (
      targetKind === "blockage" &&
      data.layer !== null &&
      typeof data.layer === "object" &&
      "layer" in data.layer
    ) {
      serializedData = {
        ...data,
        layer: (data.layer as { layer: number }).layer,
      };
    }

    return {
      targetKind,
      id: entityId,
      ...serializedData,
    };
  }

  // Get entity ID - fail if not found.
  // For graphics entities, traverses wrappers (Annotation via .data, vertex/corner via .parent)
  // to reach the underlying primitive's data.id.
  private getEntityId(entity: unknown): number | undefined {
    const e = entity as {
      id?: number;
      data?: { id?: number };
      parent?: { data?: { id?: number } };
    };
    let entityId = e.id ?? e.data?.id ?? e.parent?.data?.id;
    if (entityId === undefined) {
      logger.error("Entity missing required ID", entity);
    }
    return entityId;
  }

  // Get target kind for model change format
  private getTargetKind(entity: unknown, contentKind: string): string | null {
    const e = entity as {
      entityKind?: string;
      parent?: { entityKind?: string };
    };
    const actualEntity = e.entityKind ? e : e.parent;
    if (!actualEntity?.entityKind) {
      logger.error(
        "Entity missing required entityKind for target kind determination"
      );
      return null;
    }

    const kindMaps: Record<string, Record<string, string>> = {
      layout: {
        LayoutInstance: "instance",
        LayoutImpl: "instance",
        LayoutRoute: "route",
        LayoutPin: "pin",
        LayoutBlockage: "blockage",
      },
      schematic: {
        Instance: "instance",
        WireSegment: "wire",
        SchPin: "schpin",
        // Graphics element entity kinds — all map to the "graphics" target kind
        // (annotation text/shapes), mirroring the symbol mapping.
        Annotation: "graphics",
        Rect: "graphics",
        RectCorner: "graphics",
        Polygon: "graphics",
        PolygonVertex: "graphics",
        Path: "graphics",
        PathVertex: "graphics",
        Ellipse: "graphics",
        EllipseBody: "graphics",
        EllipseCorner: "graphics",
        Arc: "graphics",
        ArcCorner: "graphics",
        Text: "graphics",
      },
      symbol: {
        SymbolPin: "pin",
        ExprLabel: "exprlabel",
        // Graphics element entity kinds — all map to the "graphics" target kind.
        Annotation: "graphics",
        Rect: "graphics",
        RectCorner: "graphics",
        Polygon: "graphics",
        PolygonVertex: "graphics",
        Path: "graphics",
        PathVertex: "graphics",
        Ellipse: "graphics",
        EllipseBody: "graphics",
        EllipseCorner: "graphics",
        Arc: "graphics",
        ArcCorner: "graphics",
        Text: "graphics",
        SelectionBox: "selectionbox",
      },
    };

    const kindMap = kindMaps[contentKind];
    if (!kindMap) {
      logger.error(
        "Unknown content kind for target kind mapping:",
        contentKind
      );
      return null;
    }

    const targetKind = kindMap[actualEntity.entityKind];
    if (!targetKind) {
      logger.error(
        `Unknown ${contentKind} entity kind:`,
        actualEntity.entityKind
      );
      return null;
    }
    return targetKind;
  }

  handleProcessingState = (msg: msgs.ProcessingState) => {
    // Delegate to platform-specific UI handling
    // The platform should implement this through its own means
    // For now, just log it - the WebView platform will handle the UI updates

    // FIXME: that comment above dont make no sense.
    // Just handle this stuff here in the editor, not on this global state.
    logger.log(`[Editor] Processing state changed: ${msg.processing}`);
  };

  // Track parasitics mode state
  parasiticsEnabled = false;
  parasiticsSegmentCount = 0;
  parasiticsViaCount = 0;

  handleParasiticsStatusUpdate = (msg: msgs.ParasiticsStatusUpdate) => {
    this.parasiticsEnabled = msg.enabled;
    this.parasiticsSegmentCount = msg.segmentCount ?? 0;
    this.parasiticsViaCount = msg.viaCount ?? 0;

    logger.log(
      `[Editor] Parasitics mode ${msg.enabled ? "enabled" : "disabled"}` +
        (msg.enabled
          ? ` - ${this.parasiticsSegmentCount} segments, ${this.parasiticsViaCount} vias`
          : "")
    );

    // Update the visual indicator overlay
    this.updateParasiticsIndicator();
  };

  updateParasiticsIndicator = () => {
    // Update the status bar/overlay to show parasitics mode status
    if (this.parasiticsEnabled) {
      this.overlays.setStatusMessage("PEX", "parasitics");
    } else {
      this.overlays.clearStatusMessage("parasitics");
    }
  };

  updateBlockagesIndicator = () => {
    const lc = this.getLayoutContent();
    if (lc?.layoutDisplay.blockages) {
      this.overlays.setStatusMessage("Blockage", "blockage");
    } else {
      this.overlays.clearStatusMessage("blockage");
    }
  };

  // Get a `PDK` from the platform
  // Get a PDK from the platform
  getPdk = async (pdkId: string): Promise<Result<schema.pdk.PDK>> => {
    const r = await this.platform.getPdk(pdkId);
    if (r.err) {
      logger.error(`[Editor] getPdk: request failed:`, r.val);
      return Result.Err(r.val);
    }
    return Result.Ok(r.val);
  };

  // Get a Stack from the platform
  getStack = async (
    pdkId: string,
    stackId: schema.StackId
  ): Promise<Result<schema.pdk.Stack>> => {
    const r = await this.platform.getStack(pdkId, stackId);
    if (r.err) {
      logger.error(`[Editor] getStack: request failed:`, r.val);
      return Result.Err(r.val);
    }
    return Result.Ok(r.val);
  };

  // Request Content from the platform
  // (Kinda) implements `Depot.content.get()`
  getContent = async (
    ident: model.Ident
  ): Promise<Result<model.ContentJson>> => {
    const r = await this.platform.getContent(ident);
    if (r.err) {
      return Result.Err(r.val);
    }
    return Result.Ok(r.val);
  };

  requestSymbol = async (
    ident: model.Ident
  ): Promise<Result<schema.Symbol>> => {
    this.getContent(ident);
    const r = await this.getContent(ident);
    return r.andThen((c: model.ContentJson) => {
      if (c.contentKind === "symbol") return Result.Ok(c);
      return Result.Err(null); // FIXME null
    });
  };

  requestLayout = async (
    ident: model.Ident
  ): Promise<Result<schema.Layout>> => {
    const r = await this.getContent(ident);
    return r.andThen((c: model.ContentJson) => {
      if (c.contentKind === "layout") return Result.Ok(c);
      return Result.Err(null); // FIXME null
    });
  };

  // Load a new set of main content.
  // savedIdents: when refreshing because sub-design(s) changed, the idents of
  // those specific sub-designs. Used for targeted cache invalidation instead of
  // evicting all sub-layouts.
  loadMainContent = async (
    data: unknown,
    loadKind: LoadKind = LoadKind.new,
    savedIdents?: unknown[]
  ): Promise<Result<null>> => {
    this.beginLoadTiming();

    // On refresh, invalidate cached sub-layouts so they are re-fetched
    // with the latest versions. Only invalidate the specific sub-layouts
    // named in `savedIdents`. Skip invalidation for in-place state changes
    // (e.g. update-instance) where sub-layouts haven't changed — re-fetching
    // them would cause unnecessary server round-trips and ident-not-found
    // errors for generated content.
    if (
      loadKind === LoadKind.refresh &&
      savedIdents &&
      savedIdents.length > 0
    ) {
      this.invalidateSubLayoutCache(savedIdents);
    }
    // On new content, clear the static layout cache so all layouts are
    // re-parsed from fresh server data (not stale cached objects).
    if (loadKind === LoadKind.new) {
      model.layout.Layout.layouts.clear();
    }
    return (await Content.fromUnknown(this, data)).andThenAsync(
      async (content) => {
        this.loadDrawnContent(content, loadKind);
        return Result.Ok(null);
      }
    );
  };

  // Invalidate cached sub-layouts so they are re-fetched with latest versions.
  // If savedIdents is provided, only those specific sub-layouts are evicted.
  // Otherwise falls back to evicting all sub-layouts referenced by this design.
  private invalidateSubLayoutCache(savedIdents?: unknown[]): void {
    if (savedIdents && savedIdents.length > 0) {
      // Targeted invalidation: only evict the named sub-layouts. Each arrives as
      // `unknown` from the server message pipeline, so validate before use
      // rather than asserting the type.
      for (const raw of savedIdents) {
        const parsed = model.ident.Ident.validator.validate(raw);
        if (parsed.ok) model.layout.Layout.layouts.delete(parsed.val);
      }
      return;
    }
    // Broad invalidation: evict all sub-layouts this design references.
    // Used when no specific idents are available (e.g. active design self-refresh).
    if (this.content.contentKind === ContentKind.Layout) {
      for (const lay of this.content.layouts.values()) {
        model.layout.Layout.layouts.delete(lay.ident);
      }
    }
  }

  loadSchema = async (data: schema.Content): Promise<Result<null>> => {
    this.beginLoadTiming();

    return (await Content.fromSchema(this, data)).andThenAsync(
      async (content) => {
        this.loadDrawnContent(content, LoadKind.new);
        return Result.Ok(null);
      }
    );
  };

  // Load new and empty content into the editor.
  createNew = (msg: msgs.New) => {
    this.beginLoadTiming();

    const { contentKind } = msg;
    const create = Content.createNew(this, contentKind);
    if (create.err) {
      return this.error("invalid-content");
    }
    const content = create.val;
    return this.loadDrawnContent(content);
  };

  // Load `content` into the UI and draw it.
  // Addresses https://github.com/gen-alpha-xtor/GenAlphaPrivate/issues/898
  loadDrawnContent = (
    content: Content,
    loadKind: LoadKind = LoadKind.new
  ): RedrawRequest => {
    switch (loadKind) {
      case LoadKind.new:
        // Loading completely new content - reset everything
        this.content = content;
        this.syncDisplayOptions();
        // Zoom/pan to fit the new content in view.
        // In the IDE environment, EditorTabPanel calls fit() again after mount
        // with correct panel dimensions. This call is still needed for the
        // Web environment where the canvas is already in the DOM.
        this.fit();
        this.goUiIdle(); // Reset UI state to idle
        break;
      case LoadKind.refresh: {
        // Refresh content:
        // - No resetting UI state or zooming to fit the new content
        // - FIXME: we lose changeLog and undo/redo stack.
        // Carry the current selection across the content-object swap. A refresh
        // replaces `this.content` wholesale with a freshly-loaded object whose
        // selection starts empty; without this, any live selection (e.g. a
        // just-created instance racing a concurrent collaborator/Copilot/save
        // refresh) is silently dropped and the Properties panel goes stale.
        // Captured by (kind, id) so it re-resolves against the new object;
        // entities the refreshed state removed are skipped.
        const selectionKeys = this.content.captureSelectionKeys();
        // Preserve display options that the user may have toggled.
        if (
          this.content instanceof LayoutContent &&
          content instanceof LayoutContent
        ) {
          content.render.layoutDisplay = this.content.render.layoutDisplay;
        }
        if (this.content instanceof Schematic && content instanceof Schematic) {
          content.render.schematicDisplay =
            this.content.render.schematicDisplay;
        }
        this.content = content;
        this.syncDisplayOptions();
        this.content.restoreSelectionKeys(selectionKeys);
        break;
      }
    }

    // Loading is complete. Mark loadPending=false so the next redraw can
    // set fullyRendered=true and capture the time-to-first-render.
    this.fullyRendered = false;
    this.loadPending = false;
    this.elapsedStartupLoad = performance.now() - this.timeStartUp;

    return this.requestRedraw();
  };

  // Point the canvas at the current content's display options.
  // Called once when content loads — the canvas holds a shared reference,
  // so in-place mutations by toggle methods are picked up automatically
  // without per-frame copying.
  private syncDisplayOptions = (): void => {
    const lc = this.getLayoutContent();
    this.canvas.layoutDisplay = lc ? lc.layoutDisplay : null;
    const sc = this.getSchematicContent();
    this.canvas.schematicDisplay = sc ? sc.schematicDisplay : null;
  };

  // Re-draw everything.
  private redraw = () => {
    // Track the pacing time between consecutive frames:
    const perfPacing: PerfEntry = this.perf.interval("editor.pacing");

    const p = this.perf.start("editor.redraw");
    this.canvas.render((to) => {
      to.layer(this.grid.redraw);
      to.layer(this.content.redraw);
      // Draw mode-specific overlays (box-select rect, rulers, etc.)
      if (this.modeHandler.draw) {
        to.layer(this.modeHandler.draw);
      }
    });
    p.end();

    // Update the counters panel if it's visible
    if (this.uiState.counters.container !== null) {
      this.perf.update();

      if (this.pacingSmoothed === 0) {
        // Ok even if currentTime is zero, next frame will be non-zero:
        this.pacingSmoothed = perfPacing.currentTime;
      } else {
        const smoothRatio: number = 0.1;
        this.pacingSmoothed =
          (1.0 - smoothRatio) * this.pacingSmoothed +
          smoothRatio * perfPacing.currentTime;
      }

      // Exponential moving average for smooth display
      const fpsSmoothed =
        this.pacingSmoothed > 0 ? 1000 / this.pacingSmoothed : 0;

      this.uiState.counters.updateElapsed(
        this.elapsedStartupLoad,
        this.elapsedStartUpToRender,
        fpsSmoothed
      );
      if (this.canvas.stats) {
        // updateRenderStats writes the full Elements/Instances/Overlays/Arrays
        // breakdown, including the element total + max that updateElemCount
        // would write. Only fall back to updateElemCount when stats is absent.
        this.uiState.counters.updateRenderStats(this.canvas.stats);
      } else {
        this.uiState.counters.updateElemCount(0, 0);
      }
    }

    // These flags are for tracking the time-to-load-and-first-render:
    if (!this.fullyRendered && !this.loadPending) {
      this.fullyRendered = true;
      this.elapsedStartUpToRender = performance.now() - this.timeStartUp;
      this.resetFrameStats();
    }
  };

  // Fit our content to the canvas.
  // If items are selected, fit to the selection; otherwise fit to the entire scene.
  // Scales along the "larger" axis, and centers the content, measured by its `bbox`, in the canvas.
  // TODO(#2316): move this viewport math into editor_viewport.ts, as a pure
  // helper with a thin wrapper here.
  // https://github.com/gen-alpha-xtor/GenAlphaPrivate/issues/2316
  fit = () => {
    // Skip when the canvas has zero dimensions (e.g. tab hidden by dockview).
    // Computing a transform at 0x0 produces scale=0, making content invisible.
    if (this.canvas.size.width === 0 || this.canvas.size.height === 0) {
      return;
    }

    // If items are selected, fit to selection; otherwise recalculate and fit to entire scene
    const selBbox = this.content.selectionBbox();
    if (!selBbox) {
      this.content.recalcBbox();
    }
    // FIXME: javascript `floor` and `ceil`... dont understand negative numbers?
    const padded = Grid.padBbox(selBbox ?? this.content.bbox);
    this.fitToBbox(padded);
  };

  // Fit the viewport to an arbitrary bounding box.
  // Used by fit-to-view (F key) and zoom-to-box (Z key).
  // TODO(#2316): move this viewport math into editor_viewport.ts, as a pure
  // helper with a thin wrapper here.
  // https://github.com/gen-alpha-xtor/GenAlphaPrivate/issues/2316
  fitToBbox = (bbox: {
    min: { x: number; y: number };
    max: { x: number; y: number };
  }) => {
    const { min, max } = bbox;

    // Set a minimum size, lest we ever get a divide-by-zero.
    // Use a larger minimum for layouts (coordinates in nanometers) vs schematics.
    const isLayout = this.content.contentKind === ContentKind.Layout;
    const minSchSize = isLayout ? 5000 : 100; // 5000nm ~ reasonable transistor view

    const schSize = {
      width: Math.max(minSchSize, max.x - min.x),
      height: Math.max(minSchSize, max.y - min.y),
    };

    // Sort out the scale factor, first via the "larger" dimension to scale by.
    // Note scaling in the two axes is always by an equal amount, so that the aspect ratio is preserved.
    const scale = Math.min(
      this.canvas.size.width / schSize.width,
      this.canvas.size.height / schSize.height
    );

    // Create a coordinate transform with that scaling,
    // plus an offset which aligns the centers of the content (bbox) and the canvas.
    const offset = {
      x: (this.canvas.size.width - scale * (max.x + min.x)) / 2,
      y: (this.canvas.size.height + scale * (max.y + min.y)) / 2,
    };

    // And save it in the UI state.
    this.canvas.updateWorldTransforms({ scale, offset }, "schToPage");
  };

  // Show a transient feedback message in the bottom-right overlay.
  // Auto-clears after `durationMs` (default 2000ms). Rapid calls replace the previous message.
  showFeedback = (message: string, durationMs = 2000) => {
    if (this.feedbackTimer) clearTimeout(this.feedbackTimer);
    this.overlays.setStatusMessage(message, "feedback");
    this.feedbackTimer = setTimeout(() => {
      this.overlays.clearStatusMessage("feedback");
      this.feedbackTimer = null;
    }, durationMs);
  };

  // Show a transient feedback message for an operation on N items.
  // Handles pluralization: "Deleted 1 item", "Deleted 5 items".
  // Optional `suffix` is appended after the item count (e.g. " vertically").
  showItemFeedback = (verb: string, count: number, suffix = "") => {
    this.showFeedback(
      `${verb} ${count} item${count !== 1 ? "s" : ""}${suffix}`
    );
  };

  // Go to the "UI Idle" state, in which nothing is moving, being drawn, or really doing anything.
  goUiIdle = () => {
    return this.setMode(this.content.idleMode());
  };

  // Enter cursor-attached placement for the just-created, already-selected
  // entity: it follows the cursor until left-click/Enter confirms (create+move
  // fold into one undo entry) or right-click/Escape cancels (discards it).
  // No-op if the selection isn't movable.
  enterPlacementMode = (): void => {
    modes.MoveSelection.enter(this, false, true);
  };

  // Handle wheel events: two-finger scroll pans, pinch-to-zoom (or Ctrl+scroll) zooms.
  // On macOS, the browser reports pinch gestures as wheel events with ctrlKey = true.
  handleWheel = (e: WheelEvent) => {
    // Important here! Don't let the browser do its own zooming!
    e.preventDefault();
    e.stopPropagation();
    const TRACKPAD_MODE = this.trackpadMode;

    if (e.ctrlKey !== !TRACKPAD_MODE) {
      // Pinch-to-zoom (or Ctrl+scroll): zoom under cursor
      const { mousePos } = this.uiState;
      const { schToPage } = this.canvas.worldTransforms;
      const { scale } = schToPage;

      // Set our scale factor
      // FIXME: maybe have some limits on how far this goes? Ehhhhh maybe not it's fun?
      let f = 1.05;
      if (e.deltaY > 0) {
        f = 1 / f;
      }

      // Zoom such that the same point on the schematic is under the mouse before and after the zoom.
      // so `schToPage1(mousePos.sch) == schToPage2(mousePos.sch)`
      const offset = {
        x: schToPage.offset.x + (1 - f) * scale * mousePos.sch.x,
        y: schToPage.offset.y - (1 - f) * scale * mousePos.sch.y, // Note the y-inversion!
      };

      this.canvas.updateWorldTransforms(
        { scale: f * scale, offset },
        "schToPage"
      );

      // Grid is now viewport-computed per frame, no explicit update needed.
    } else {
      // Two-finger scroll (or mouse wheel): pan viewport
      const { schToPage } = this.canvas.worldTransforms;

      const offset = {
        x: schToPage.offset.x - e.deltaX,
        y: schToPage.offset.y - e.deltaY,
      };

      this.canvas.updateWorldTransforms(
        { scale: schToPage.scale, offset },
        "schToPage"
      );

      // Refresh the cached mouse position so the schematic coordinates are
      // recomputed against the new world transforms. Without this, a
      // subsequent zoom would center on the stale pre-pan position.
      this.uiState.refreshMousePos();
    }
  };
  // Handle keystrokes.
  handleKey = (e: KeyboardEvent) => {
    // When a text input has focus, don't interpret keys as viewport
    // shortcuts. The event should go to the input element instead.
    if (isTextInputFocused()) return;
    if (e.key === Keys.T) {
      this.trackpadMode = !this.trackpadMode;
      logger.log(`Trackpad mode: ${this.trackpadMode ? "ON" : "OFF"}`);
      return;
    }
    if (e.key === Keys.G) {
      return this.grid.toggle();
    }
    if (e.key === Keys.O) {
      this.showOutlines = !this.showOutlines;
      if (
        this.showOutlines &&
        this.content.contentKind === ContentKind.Layout
      ) {
        this.content.initOutline();
      }
      return this.requestRedraw();
    }
    // Theme toggling (t key) is handled by the IDE command palette.
    // The `Keys.Six` alternate is removed.
    if (e.key === Keys.f) {
      const hasSelection = this.content.selectionBbox() !== null;
      this.fit();
      this.showFeedback(
        hasSelection ? "Zoomed to fit selection" : "Zoomed to fit design"
      );
      return;
    }
    // Layer info (L key), show-all-layers (A key), keyboard help (? key),
    // pin labels (7 key), and instance names (8 key) are all handled by the
    // IDE's CommandCenter, not here. See commandCenter.ts for these commands.
    // Always abort any pending operation and go back to idle mode on escape.
    if (e.key === Keys.Escape) {
      return this.modeHandler.abort();
    }
    // When locked (e.g., LLM query in flight), block all design-modifying keys
    // below this point. Navigation and view-toggle keys above are still allowed.
    if (this.isLocked) return;
    // Modifier-key shortcuts: Cmd (Mac) / Ctrl (Win/Linux)
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === Keys.z) {
      // Command-Shift-Z (Mac) / Ctrl-Shift-Z (Win): redo
      const result = this.content.redo();
      if (result) this.showFeedback("Redo");
      return result;
    }
    if ((e.metaKey || e.ctrlKey) && e.key === Keys.z) {
      // Command-Z (Mac) / Ctrl-Z (Win): undo
      const result = this.content.undo();
      if (result) this.showFeedback("Undo");
      return result;
    }
    if (
      (e.metaKey || e.ctrlKey) &&
      e.shiftKey &&
      (e.key === Keys.l || e.key === Keys.L)
    ) {
      // Command-Shift-L (Mac) / Ctrl-Shift-L (Win): generate layout from source
      this.platform.notifyGenerateFromSource();
      return;
    }
    if (
      (e.metaKey || e.ctrlKey) &&
      !e.shiftKey &&
      !e.altKey &&
      (e.key === Keys.c || e.key === Keys.v)
    ) {
      // Cmd+C / Cmd+V: forward to the mode handler (e.g. DrawRoute copy/paste).
      return this.modeHandler.handleKey(e);
    }
    if (e.ctrlKey || e.metaKey || e.altKey) {
      // Skip everything else when a modifier key is pressed.
      // IDE-level shortcuts (e.g. Cmd/Ctrl+S for save) are handled by
      // the host application's own keydown listener.
      return;
    }

    // Delegate everything else to the mode-specific key handler.
    return this.modeHandler.handleKey(e);
  };

  // Hit testing. Delegated to the constituent `Content`.
  whatdWeHit = (mousePos: MousePos) => this.content.whatdWeHit(mousePos);
  deselect = () => this.content.deselect();
  selectAll = () => this.content.selectAll();

  // Toggle Draw Route mode (Shift+R in layout). If already in DrawRoute, exits it.
  // Safe to call on any content type — no-ops when not in a layout view.
  toggleDrawRoute = (): void => {
    if (this.modeHandler instanceof DrawRoute) {
      this.modeHandler.abort();
    } else {
      DrawRoute.enter(this);
    }
  };

  // Apply the pending draw width for DrawRoute mode (from the width dialog).
  // No-op when not currently in DrawRoute mode.
  applyDrawRouteWidth = (width: number | null): void => {
    if (this.modeHandler instanceof DrawRoute) {
      this.modeHandler.applyDrawWidth(width);
    }
  };

  // FIXME: whether we want a "click" handler, in addition to mouse up/down.
  // handleClick = e => {}

  // Handle mouse-down events.
  handleMouseDown = (e: MouseEvent) => {
    // When locked, only allow middle-click (pan). Block left-click interactions
    // that would modify the design.
    if (this.isLocked && e.button !== 1) return;
    // Fully delegated to the mode-handlers.
    this.modeHandler.handleMouseDown(e);
  };
  // Handle mouse-up events. Fully delegated to the mode-handlers.
  handleMouseUp = (_: MouseEvent) => this.modeHandler.handleMouseUp();
  // Handle double-click events. Fully delegated to the mode-handlers.
  handleDoubleClick = (_: MouseEvent) => this.modeHandler.handleDoubleClick();
  // Handle mouse movement events.
  handleMouseMove = (e: MouseEvent) => {
    // Note that we do not need to lock out mouse movement when the design is locked,
    // as mouse movement itself does not modify the design. This allows users to still
    // pan around and navigate while locked.

    // Update our tracking of the mouse position.
    this.uiState.updateMousePos(e);

    // Update tooltip hover state - DISABLED: now using context menu instead
    // this.instanceTooltip.updateHover();

    // And delegate to the mode-handler.
    return this.modeHandler.handleMouseMove();
  };

  // Get the nearest grid point to the mouse.
  gridPointNearestToMouse = (): schdata.Point => {
    return this.grid.nearestPoint(this.uiState.mousePos.sch);
  };

  // Update the schematic's code-prelude.
  // FIXME: remove this
  updateCodePrelude = (codePrelude: string) => {};

  // Update editor settings
  updateSettings = (settings: msgs.Settings) => {
    const { gridMinor, gridSnap, gridMultiple, theme } = settings;
    this.grid.updateSettings({
      minor: gridMinor,
      snap: gridSnap,
      multiple: gridMultiple,
    });
    // Apply theme from VSCode if provided
    if (theme) {
      switch (theme) {
        case "light":
          this.setTheme(Theme.Light);
          break;
        case "dark":
          this.setTheme(Theme.Dark);
          break;
        case "high-contrast":
          this.setTheme(Theme.HighContrast);
          break;
        case "high-contrast-light":
          this.setTheme(Theme.HighContrastLight);
          break;
      }
    }
  };

  // Update our mode-specific handler
  setMode = (mode: modes.ModeHandler): modes.ModeHandler => {
    this.modeHandler = mode;
    return mode;
  };

  // Start the self-test animation
  selfTest = () => {
    if (this.selfTester) return;
    // Create and start a new self-tester
    this.selfTester = new SelfTest(this).start();
  };

  // Get aggregate performance stats from built-in perf counters.
  getFPSAll = (): {
    timeStartup: number;
    averageRenderTime: number;
    totalRenderDeviationTime: number;
    averagePacingTime: number;
    totalPacingDeviationTime: number;
    countFrames: number;
    countHitchFrames: number;
    worstRenderTime: number;
  } => {
    let frames: number = 0;
    let avgRenderTime: number = 0;
    let totalDeviance: number = 0;
    let countHitchFrames: number = 0;
    let worstRenderTime: number = 0;

    const redraw = this.perf.get("editor.redraw");
    if (redraw !== undefined) {
      frames = redraw.count;
      avgRenderTime = redraw.avg();
      totalDeviance = redraw.totalDeviance;
      countHitchFrames = redraw.countHitches;
      worstRenderTime = redraw.maxTime;
    }

    let averagePacingTime: number = 0;
    let totalPacingDeviance: number = 0;

    const pacing = this.perf.get("editor.pacing");
    if (pacing !== undefined) {
      averagePacingTime = pacing.avg();
      totalPacingDeviance = pacing.totalDeviance;
    }

    return {
      timeStartup: this.elapsedStartUpToRender,
      averageRenderTime: avgRenderTime,
      totalRenderDeviationTime: totalDeviance,
      averagePacingTime,
      totalPacingDeviationTime: totalPacingDeviance,
      countFrames: frames,
      countHitchFrames,
      worstRenderTime,
    };
  };

  // The ID of the DOM element we expect to be mounted in.
  // Note this is probably no longer a required thing, as we get the DOM element itself from the platform.
  // Nonetheless, leaving it here, platforms can continue to name the element this way.
  static readonly PARENT_DOM_ID = "GENALPHA_EDITOR";
  readonly parentDomId = Editor.PARENT_DOM_ID;

  /**
   * Highlight layout elements for LVS error visualization.
   * Pass null to clear all highlights.
   * No-op if the current content is not a layout.
   */
  highlightLvsError = (info: LvsErrorInfo | null): RedrawRequest => {
    if (this.content.contentKind !== ContentKind.Layout) {
      return this.requestRedraw();
    }
    return this.content.highlightLvsError(info);
  };

  /**
   * Highlight layout elements for DRC error visualization.
   * Pass null to clear all highlights.
   * No-op if the current content is not a layout.
   */
  highlightDrcError = (info: DrcErrorInfo | null): RedrawRequest => {
    if (this.content.contentKind !== ContentKind.Layout) {
      return this.requestRedraw();
    }
    return this.content.highlightDrcError(info);
  };

  /** Labelled boxes over a layout; none to clear them. No-op on a schematic. */
  setLayoutMarkers = (markers: ReadonlyArray<LayoutMarker>): RedrawRequest => {
    if (this.content.contentKind !== ContentKind.Layout) {
      return this.requestRedraw();
    }
    return this.content.setMarkers(markers);
  };

  /**
   * Push the SRV arrow overlay onto the active schematic.
   * No-op if the current content is not a schematic. Use `clearSrvOverlay` to
   * remove an existing overlay.
   */
  setSrvOverlay = (overlay: SrvOverlay): RedrawRequest => {
    const schematic = this.getSchematicContent();
    if (schematic === null) return this.requestRedraw();
    return schematic.setSrvOverlay(overlay);
  };

  /**
   * Clear any SRV arrow overlay on the active schematic.
   * No-op if the current content is not a schematic.
   */
  clearSrvOverlay = (): RedrawRequest => {
    const schematic = this.getSchematicContent();
    if (schematic === null) return this.requestRedraw();
    return schematic.clearSrvOverlay();
  };

  /**
   * Highlight routes for parasitics net visualization.
   * Pass null to clear all highlights.
   * Pass one net name to highlight a single net.
   * Pass two net names to highlight a coupling pair.
   * No-op if the current content is not a layout.
   */
  highlightParasiticsNet = (
    netA: string | null,
    netB?: string
  ): RedrawRequest => {
    if (this.content.contentKind !== ContentKind.Layout) {
      return this.requestRedraw();
    }
    return this.content.highlightParasiticsNet(netA, netB);
  };
}

// Give `window` a reference to us for debugging. Or at least a field for it.
declare global {
  interface Window {
    genAlphaEditor: Editor | null;
    showOpenFilePicker: () => Promise<any>;
    showSaveFilePicker: () => Promise<any>;
  }
}
